import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

const PyrotechnicBackground = ({ variant = 'default', intensity = 'medium' }) => {
  const canvasRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationId;

    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      setDimensions({ width: canvas.width, height: canvas.height });
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Pyrotechnic particle system
    class PyrotechnicParticle {
      constructor(x, y, type = 'spark') {
        this.x = x || Math.random() * canvas.width;
        this.y = y || canvas.height + 10;
        this.type = type;
        this.reset();
      }

      reset() {
        if (this.type === 'spark') {
          this.vx = (Math.random() - 0.5) * 4;
          this.vy = -Math.random() * 8 - 2;
          this.life = 1;
          this.decay = Math.random() * 0.02 + 0.005;
          this.size = Math.random() * 3 + 1;
          this.color = this.getSparkColor();
          this.trail = [];
        } else if (this.type === 'firework') {
          this.vx = (Math.random() - 0.5) * 6;
          this.vy = -Math.random() * 12 - 4;
          this.life = 1;
          this.decay = Math.random() * 0.015 + 0.003;
          this.size = Math.random() * 4 + 2;
          this.color = this.getFireworkColor();
          this.exploded = false;
          this.explodeHeight = Math.random() * canvas.height * 0.3 + canvas.height * 0.2;
        } else if (this.type === 'glow') {
          this.vx = (Math.random() - 0.5) * 2;
          this.vy = -Math.random() * 3 - 1;
          this.life = 1;
          this.decay = Math.random() * 0.008 + 0.002;
          this.size = Math.random() * 6 + 3;
          this.color = this.getGlowColor();
          this.pulse = Math.random() * Math.PI * 2;
        }
      }

      getSparkColor() {
        const colors = [
          '#0ea5e9', '#8b5cf6', '#06b6d4', '#10b981', 
          '#f59e0b', '#ef4444', '#ec4899', '#84cc16'
        ];
        return colors[Math.floor(Math.random() * colors.length)];
      }

      getFireworkColor() {
        const colors = [
          '#3b82f6', '#8b5cf6', '#06b6d4', '#10b981',
          '#f59e0b', '#ef4444', '#ec4899', '#a855f7'
        ];
        return colors[Math.floor(Math.random() * colors.length)];
      }

      getGlowColor() {
        const colors = [
          'rgba(14, 165, 233, 0.6)', 
          'rgba(139, 92, 246, 0.6)', 
          'rgba(6, 182, 212, 0.6)',
          'rgba(16, 185, 129, 0.6)'
        ];
        return colors[Math.floor(Math.random() * colors.length)];
      }

      update() {
        if (this.type === 'firework' && !this.exploded && this.y <= this.explodeHeight) {
          this.explode();
          return false; // Remove this particle
        }

        this.x += this.vx;
        this.y += this.vy;
        this.vy += 0.1; // gravity
        this.life -= this.decay;

        if (this.type === 'spark' && this.trail) {
          this.trail.push({ x: this.x, y: this.y, life: 0.8 });
          if (this.trail.length > 10) this.trail.shift();
          
          // Update trail
          this.trail.forEach(point => {
            point.life -= 0.1;
          });
          this.trail = this.trail.filter(point => point.life > 0);
        }

        if (this.type === 'glow') {
          this.pulse += 0.1;
          this.vx *= 0.99;
          this.vy *= 0.99;
        }

        return this.life > 0 && this.y < canvas.height + 50;
      }

      explode() {
        // Create explosion particles
        for (let i = 0; i < 15; i++) {
          const spark = new PyrotechnicParticle(this.x, this.y, 'spark');
          spark.vx = (Math.random() - 0.5) * 8;
          spark.vy = (Math.random() - 0.5) * 8;
          spark.color = this.color;
          spark.size = Math.random() * 2 + 1;
          particles.push(spark);
        }
      }

      draw() {
        ctx.save();
        ctx.globalAlpha = this.life;

        if (this.type === 'spark') {
          // Draw trail
          if (this.trail && this.trail.length > 1) {
            ctx.strokeStyle = this.color;
            ctx.lineWidth = this.size * 0.5;
            ctx.beginPath();
            for (let i = 0; i < this.trail.length - 1; i++) {
              const current = this.trail[i];
              const next = this.trail[i + 1];
              ctx.globalAlpha = current.life * this.life;
              ctx.moveTo(current.x, current.y);
              ctx.lineTo(next.x, next.y);
            }
            ctx.stroke();
          }

          // Draw spark
          ctx.globalAlpha = this.life;
          ctx.fillStyle = this.color;
          ctx.shadowBlur = 15;
          ctx.shadowColor = this.color;
          ctx.beginPath();
          ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
          ctx.fill();

        } else if (this.type === 'firework') {
          ctx.fillStyle = this.color;
          ctx.shadowBlur = 20;
          ctx.shadowColor = this.color;
          ctx.beginPath();
          ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
          ctx.fill();

        } else if (this.type === 'glow') {
          const pulseFactor = Math.sin(this.pulse) * 0.3 + 0.7;
          const gradient = ctx.createRadialGradient(
            this.x, this.y, 0,
            this.x, this.y, this.size * pulseFactor
          );
          gradient.addColorStop(0, this.color);
          gradient.addColorStop(1, 'transparent');
          
          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(this.x, this.y, this.size * pulseFactor, 0, Math.PI * 2);
          ctx.fill();
        }

        ctx.restore();
      }
    }

    // Energy wave effect
    class EnergyWave {
      constructor() {
        this.reset();
      }

      reset() {
        this.x = Math.random() * canvas.width;
        this.y = canvas.height + 50;
        this.radius = 0;
        this.maxRadius = Math.random() * 200 + 100;
        this.speed = Math.random() * 2 + 1;
        this.life = 1;
        this.decay = 0.005;
        this.color = `hsl(${Math.random() * 60 + 200}, 70%, 60%)`;
      }

      update() {
        this.radius += this.speed;
        this.life -= this.decay;
        this.y -= 1;

        return this.life > 0 && this.radius < this.maxRadius;
      }

      draw() {
        ctx.save();
        ctx.globalAlpha = this.life * 0.3;
        ctx.strokeStyle = this.color;
        ctx.lineWidth = 2;
        ctx.shadowBlur = 15;
        ctx.shadowColor = this.color;
        
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.stroke();
        
        ctx.restore();
      }
    }

    // Lightning effect
    class Lightning {
      constructor() {
        this.reset();
      }

      reset() {
        this.startX = Math.random() * canvas.width;
        this.startY = 0;
        this.endX = this.startX + (Math.random() - 0.5) * 200;
        this.endY = Math.random() * canvas.height * 0.3;
        this.segments = this.generateSegments();
        this.life = 1;
        this.decay = 0.05;
        this.color = '#ffffff';
      }

      generateSegments() {
        const segments = [];
        const numSegments = 8;
        const dx = (this.endX - this.startX) / numSegments;
        const dy = (this.endY - this.startY) / numSegments;

        for (let i = 0; i <= numSegments; i++) {
          const x = this.startX + dx * i + (Math.random() - 0.5) * 30;
          const y = this.startY + dy * i + (Math.random() - 0.5) * 20;
          segments.push({ x, y });
        }

        return segments;
      }

      update() {
        this.life -= this.decay;
        return this.life > 0;
      }

      draw() {
        ctx.save();
        ctx.globalAlpha = this.life;
        ctx.strokeStyle = this.color;
        ctx.lineWidth = 3;
        ctx.shadowBlur = 20;
        ctx.shadowColor = '#ffffff';
        
        ctx.beginPath();
        ctx.moveTo(this.segments[0].x, this.segments[0].y);
        
        for (let i = 1; i < this.segments.length; i++) {
          ctx.lineTo(this.segments[i].x, this.segments[i].y);
        }
        
        ctx.stroke();
        ctx.restore();
      }
    }

    // Particle systems
    const particles = [];
    const energyWaves = [];
    const lightnings = [];

    const particleCount = intensity === 'low' ? 30 : intensity === 'high' ? 80 : 50;

    // Initialize particles
    for (let i = 0; i < particleCount; i++) {
      const types = ['spark', 'glow'];
      if (Math.random() < 0.1) types.push('firework');
      
      const type = types[Math.floor(Math.random() * types.length)];
      particles.push(new PyrotechnicParticle(
        Math.random() * canvas.width,
        Math.random() * canvas.height,
        type
      ));
    }

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Add new particles occasionally
      if (Math.random() < 0.1) {
        const type = Math.random() < 0.7 ? 'spark' : Math.random() < 0.9 ? 'glow' : 'firework';
        particles.push(new PyrotechnicParticle(
          Math.random() * canvas.width,
          canvas.height + 10,
          type
        ));
      }

      // Add energy waves
      if (Math.random() < 0.02) {
        energyWaves.push(new EnergyWave());
      }

      // Add lightning occasionally
      if (Math.random() < 0.005) {
        lightnings.push(new Lightning());
      }

      // Update and draw energy waves
      for (let i = energyWaves.length - 1; i >= 0; i--) {
        if (!energyWaves[i].update()) {
          energyWaves.splice(i, 1);
        } else {
          energyWaves[i].draw();
        }
      }

      // Update and draw lightning
      for (let i = lightnings.length - 1; i >= 0; i--) {
        if (!lightnings[i].update()) {
          lightnings.splice(i, 1);
        } else {
          lightnings[i].draw();
        }
      }

      // Update and draw particles
      for (let i = particles.length - 1; i >= 0; i--) {
        if (!particles[i].update()) {
          particles[i].reset();
          particles[i].x = Math.random() * canvas.width;
          particles[i].y = canvas.height + 10;
        } else {
          particles[i].draw();
        }
      }

      // Limit particle count
      if (particles.length > particleCount * 1.5) {
        particles.splice(0, particles.length - particleCount);
      }

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [variant, intensity]);

  return (
    <>
      <canvas 
        ref={canvasRef}
        className="fixed inset-0 pointer-events-none z-0"
        style={{ opacity: 0.6 }}
      />
      
      {/* Additional CSS-based pyrotechnic effects */}
      <div className="fixed inset-0 pointer-events-none z-0">
        {/* Floating energy orbs */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-32 h-32 rounded-full"
            style={{
              background: `radial-gradient(circle, rgba(14, 165, 233, 0.3) 0%, rgba(139, 92, 246, 0.2) 50%, transparent 100%)`,
              left: `${10 + i * 15}%`,
              top: `${10 + (i % 3) * 30}%`,
            }}
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.3, 0.7, 0.3],
              rotate: [0, 360],
            }}
            transition={{
              duration: 8 + i * 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}

        {/* Pulsing grid */}
        <div className="absolute inset-0 opacity-20">
          <div 
            className="w-full h-full"
            style={{
              backgroundImage: `
                radial-gradient(circle at 25% 25%, rgba(14, 165, 233, 0.4) 0%, transparent 50%),
                radial-gradient(circle at 75% 75%, rgba(139, 92, 246, 0.4) 0%, transparent 50%),
                radial-gradient(circle at 75% 25%, rgba(6, 182, 212, 0.4) 0%, transparent 50%),
                radial-gradient(circle at 25% 75%, rgba(16, 185, 129, 0.4) 0%, transparent 50%)
              `,
              animation: 'pulseGrid 8s ease-in-out infinite',
            }}
          />
        </div>

        {/* Shooting stars */}
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={`star-${i}`}
            className="absolute w-2 h-2 bg-gradient-to-r from-white via-blue-400 to-transparent rounded-full"
            style={{
              boxShadow: '0 0 20px rgba(255, 255, 255, 0.8)',
            }}
            initial={{
              x: -100,
              y: Math.random() * window.innerHeight * 0.5,
              opacity: 0,
            }}
            animate={{
              x: window.innerWidth + 100,
              y: Math.random() * window.innerHeight * 0.5 + 200,
              opacity: [0, 1, 1, 0],
            }}
            transition={{
              duration: 3,
              delay: i * 4,
              repeat: Infinity,
              repeatDelay: 8,
              ease: "easeInOut",
            }}
          />
        ))}

        {/* Energy pulses */}
        {[...Array(4)].map((_, i) => (
          <motion.div
            key={`pulse-${i}`}
            className="absolute border-2 border-primary-400 rounded-full"
            style={{
              left: `${20 + i * 20}%`,
              top: `${30 + (i % 2) * 40}%`,
              width: '100px',
              height: '100px',
            }}
            animate={{
              scale: [1, 3, 1],
              opacity: [0.8, 0.2, 0.8],
              rotate: [0, 180, 360],
            }}
            transition={{
              duration: 4,
              delay: i * 1,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>
    </>
  );
};

export default PyrotechnicBackground;